import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import ReactSelect from 'react-select';

export type MultiSelectPickerOption<T extends string | number = number> = {
  value: T;
  label: string;
};

export type MultiSelectPickerProps<T extends string | number = number> = {
  id: string;
  label?: string;
  value?: T[];
  onChange: (value: T[]) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  options: MultiSelectPickerOption<T>[];
  onMenuScrollToBottom?: () => void;
  onSearchChange?: (value: string) => void;
  serverSideSearch?: boolean;
};

export default function MultiSelectPicker<T extends string | number = number>({
  id,
  label,
  value = [],
  onChange,
  placeholder,
  disabled = false,
  loading = false,
  options,
  onMenuScrollToBottom,
  onSearchChange,
  serverSideSearch = false,
}: MultiSelectPickerProps<T>) {
  const selectedValue = useMemo(() => options.filter((opt) => (value ?? []).includes(opt.value)), [options, value]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {!!label && (
        <Typography
          variant='body2'
          sx={{ fontWeight: 500 }}
        >
          {label}
        </Typography>
      )}
      <ReactSelect<MultiSelectPickerOption<T>, true>
        name={id}
        value={selectedValue}
        options={options}
        placeholder={placeholder}
        isMulti
        isSearchable
        isDisabled={disabled}
        isLoading={loading}
        classNamePrefix='react-select'
        menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
        filterOption={serverSideSearch ? () => true : undefined}
        components={{
          LoadingIndicator: () => (
            <CircularProgress
              size={16}
              sx={{ mr: 1 }}
            />
          ),
        }}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (base) => ({ ...base, zIndex: 9999 }),
          input: (base) => ({
            ...base,
            boxShadow: 'none',
            outline: 0,
            minHeight: 45,
          }),
        }}
        onMenuScrollToBottom={onMenuScrollToBottom}
        onInputChange={(newValue, actionMeta) => {
          if (!onSearchChange) return;
          if (actionMeta.action === 'input-change') {
            onSearchChange(newValue);
          } else if (actionMeta.action === 'menu-close' || actionMeta.action === 'set-value') {
            onSearchChange('');
          }
        }}
        onChange={(newValue) => {
          const arr = newValue ?? [];
          onChange(arr.map((x) => x.value));
        }}
      />
    </Box>
  );
}
